import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SmsService } from 'src/app/services/sms.service';
import { SmsDraftService, SmsDraft } from 'src/app/services/sms-draft.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

declare var $: any;

@Component({
  selector: 'app-quick-sms-service',
  templateUrl: './quick-sms-service.component.html',
  styleUrls: ['./quick-sms-service.component.scss']
})
export class QuickSmsServiceComponent implements OnInit, OnDestroy {
  smsForm!: FormGroup;
  message: string = '';
  recipients: string = '';
  charCount: number = 0;
  smsCount: number = 0;
  isLoading: boolean = false;
  isPreviewMode: boolean = false;

  // Recipients preview
  showRecipientsPreview: boolean = false;

  // Draft Management
  drafts: SmsDraft[] = [];
  currentDraft: SmsDraft | null = null;
  isLoadingDrafts: boolean = false;
  isDraftMode: boolean = false;
  showDraftModal: boolean = false;
  draftSearchTerm: string = '';
  showFavoritesOnly: boolean = false;
  draftName: string = '';
  
  // Auto-save
  private destroy$ = new Subject<void>();
  private autoSaveSubject = new Subject<void>();
  isAutoSaving: boolean = false;

  // Form change tracking
  private originalDraftValues: { recipients: string; message: string } | null = null;
  hasFormChanges: boolean = false;

  // Pagination for drafts
  currentPage: number = 1;
  totalPages: number = 1;
  totalDrafts: number = 0;

  constructor(
    private fb: FormBuilder,
    private smsService: SmsService,
    private smsDraftService: SmsDraftService,
    private toastr: ToastrService
  ) {
    this.initializeForm();
  }

  ngOnInit() {
    this.loadDrafts();
    this.setupAutoSave();
    this.loadFromLocalStorage(); // Fallback for existing data
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm() {
    this.smsForm = this.fb.group({
      recipients: ['', [Validators.required]],
      message: ['', [Validators.required, Validators.maxLength(1000)]]
    });

    // Watch for form changes
    this.smsForm.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(values => {
      this.recipients = values.recipients;
      this.message = values.message;
      this.updateCharacterCount();
      this.checkForChanges();
      this.triggerAutoSave();
    });
  }

  private setupAutoSave() {
    this.autoSaveSubject.pipe(
      debounceTime(2000), // Auto-save after 2 seconds of inactivity
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.currentDraft && this.canAutoSave()) {
        this.performAutoSave();
      }
    });
  }

  // Check for form changes compared to original draft values
  private checkForChanges() {
    if (!this.originalDraftValues) {
      this.hasFormChanges = false;
      return;
    }

    const currentValues = {
      recipients: this.recipients || '',
      message: this.message || ''
    };

    this.hasFormChanges = 
      currentValues.recipients !== this.originalDraftValues.recipients ||
      currentValues.message !== this.originalDraftValues.message;
  }

  // Store original draft values for change tracking
  private storeOriginalDraftValues() {
    this.originalDraftValues = {
      recipients: this.recipients || '',
      message: this.message || ''
    };
    this.hasFormChanges = false;
  }

  // Clear original draft values tracking
  private clearOriginalDraftValues() {
    this.originalDraftValues = null;
    this.hasFormChanges = false;
  }

  private triggerAutoSave() {
    if (this.currentDraft) {
      this.autoSaveSubject.next();
    }
  }

  private canAutoSave(): boolean {
    return !this.isAutoSaving && 
           this.smsForm.valid && 
           (this.message.trim().length > 0 || this.recipients.trim().length > 0);
  }

  private performAutoSave() {
    if (!this.currentDraft?.id) return;

    this.isAutoSaving = true;
    const updateData: Partial<SmsDraft> = {
      recipients: this.recipients,
      message: this.message
    };

    this.smsDraftService.updateDraft(this.currentDraft.id, updateData).subscribe({
      next: () => {
        this.isAutoSaving = false;
        this.toastr.success('Draft auto-saved');
      },
      error: (error) => {
        this.isAutoSaving = false;
        this.toastr.error('Auto-save failed:', error);
      }
    });
  }

  // Load drafts from backend
  loadDrafts() {
    this.isLoadingDrafts = true;
    this.smsDraftService.getAllDrafts(
      this.currentPage, 
      10, 
      this.draftSearchTerm, 
      this.showFavoritesOnly
    ).subscribe({
      next: (response) => {
        this.drafts = response.drafts;
        this.totalDrafts = response.total;
        this.totalPages = response.total_pages;
        this.isLoadingDrafts = false;
      },
      error: (error) => {
        console.error('Error loading drafts:', error);
        this.isLoadingDrafts = false;
      }
    });
  }

  // Search drafts
  searchDrafts() {
    this.currentPage = 1;
    this.loadDrafts();
  }

  // Toggle favorites filter
  toggleFavoritesFilter() {
    this.showFavoritesOnly = !this.showFavoritesOnly;
    this.currentPage = 1;
    this.loadDrafts();
  }

  // Pagination
  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadDrafts();
    }
  }

  // Create new draft
  createNewDraft() {
    this.isDraftMode = true;
    this.currentDraft = {
      name: '',
      recipients: this.recipients,
      message: this.message
    };
    this.draftName = '';
    this.showDraftModal = true;
  }

  // Close draft modal
  closeDraftModal() {
    this.showDraftModal = false;
    this.draftName = '';
    this.currentDraft = null;
  }

  // Save draft with the entered name
  saveDraftWithName() {
    if (!this.draftName.trim()) {
      alert('খসড়ার নাম দিন!');
      return;
    }

    const draftData: SmsDraft = {
      name: this.draftName.trim(),
      recipients: this.recipients,
      message: this.message,
      is_favorite: false
    };

    this.smsDraftService.createDraft(draftData).subscribe({
      next: (response) => {
        this.loadDrafts();
        this.closeDraftModal();
        this.isDraftMode = false;
        this.toastr.success('খসড়া সফলভাবে সংরক্ষিত হয়েছে!');
      },
      error: (error) => {
        console.error('Error saving draft:', error);
        this.toastr.error('খসড়া সংরক্ষণে ত্রুটি হয়েছে!');
      }
    });
  }



  // Load draft
  loadDraft(draft: SmsDraft) {
    this.currentDraft = draft;
    this.smsForm.patchValue({
      recipients: draft.recipients,
      message: draft.message
    });
    this.isDraftMode = true;
    
    // Store original values for change tracking
    this.storeOriginalDraftValues();
    
    // Show success notification
    this.toastr.info(`"${draft.name}" খসড়া লোড হয়েছে`, 'সম্পাদনা মোড');
    
    // Smooth scroll to form
    setTimeout(() => {
      const formElement = document.getElementById('sms-compose-form');
      if (formElement) {
        formElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
    
    // Update last_used timestamp
    if (draft.id) {
      this.smsDraftService.getDraftById(draft.id).subscribe();
    }
  }

  // Update current draft
  updateCurrentDraft() {
    if (!this.currentDraft?.id) return;

    const updateData: Partial<SmsDraft> = {
      recipients: this.recipients,
      message: this.message
    };

    this.smsDraftService.updateDraft(this.currentDraft.id, updateData).subscribe({
      next: () => {
        this.loadDrafts();
        // Reset change tracking after successful update
        this.storeOriginalDraftValues();
        this.toastr.success(`"${this.currentDraft?.name}" খসড়া আপডেট হয়েছে!`);
      },
      error: (error) => {
        console.error('Error updating draft:', error);
        this.toastr.error('খসড়া আপডেটে ত্রুটি হয়েছে!');
      }
    });
  }

  // Delete draft
  deleteDraft(draft: SmsDraft) {
    if (confirm(`"${draft.name}" খসড়াটি মুছে ফেলবেন?`)) {
      if (draft.id) {
        this.smsDraftService.deleteDraft(draft.id).subscribe({
          next: () => {
            this.loadDrafts();
            if (this.currentDraft?.id === draft.id) {
              this.currentDraft = null;
              this.isDraftMode = false;
            }
          },
          error: (error) => {
            console.error('Error deleting draft:', error);
            this.toastr.error('খসড়া মুছতে ত্রুটি হয়েছে!');
          }
        });
      }
    }
  }

  // Toggle favorite
  toggleFavorite(draft: SmsDraft) {
    if (draft.id) {
      this.smsDraftService.toggleFavorite(draft.id).subscribe({
        next: () => {
          this.loadDrafts();
        },
        error: (error) => {
          console.error('Error toggling favorite:', error);
        }
      });
    }
  }

  // Clear form and reset states
  clearForm() {
    this.smsForm.reset();
    this.recipients = '';
    this.message = '';
    this.charCount = 0;
    this.smsCount = 0;
    this.currentDraft = null;
    this.isDraftMode = false;
    this.clearOriginalDraftValues();
    this.toastr.info('ফর্ম পরিষ্কার করা হয়েছে');
  }
  
  // Exit draft mode without clearing form
  exitDraftMode() {
    this.isDraftMode = false;
    this.currentDraft = null;
    this.clearOriginalDraftValues();
    this.toastr.info('খসড়া মোড বন্ধ করা হয়েছে');
  }

  // Character count and SMS calculation
  updateCharacterCount() {
    this.charCount = this.message.length;
    this.smsCount = Math.ceil(this.charCount / 160) || 0;
  }

  // Get total recipients count
  getTotalRecipients(): number {
    if (!this.recipients) return 0;
    return this.recipients.split(',').filter(num => num.trim().length > 0).length;
  }

  // Get valid recipients (Bangladesh mobile numbers)
  getValidRecipients(): string[] {
    if (!this.recipients) return [];
    const numbers = this.recipients.split(',').map(num => num.trim()).filter(num => num.length > 0);
    return numbers.filter(num => this.isValidBangladeshNumber(num));
  }

  // Get invalid recipients
  getInvalidRecipients(): string[] {
    if (!this.recipients) return [];
    const numbers = this.recipients.split(',').map(num => num.trim()).filter(num => num.length > 0);
    return numbers.filter(num => !this.isValidBangladeshNumber(num));
  }

  // Validate Bangladesh mobile number
  private isValidBangladeshNumber(number: string): boolean {
    // Remove any spaces or special characters
    const cleanNumber = number.replace(/[\s\-\(\)]/g, '');
    
    // Bangladesh mobile number pattern: starts with 01 and has 11 digits total
    const bangladeshMobilePattern = /^01[3-9]\d{8}$/;
    
    return bangladeshMobilePattern.test(cleanNumber);
  }

  // Send SMS
  sendSMS() {
    if (this.smsForm.invalid) {
      alert('সকল তথ্য সঠিকভাবে পূরণ করুন!');
      return;
    }

    if (confirm(`${this.getTotalRecipients()} জনকে SMS পাঠাবেন?`)) {
      this.isLoading = true;
      
      const smsData = {
        recipients: this.recipients,
        message: this.message
      };

      try {
        this.smsService.sendQuickSmsToMultipleNumbers(
          this.recipients,
          this.message
        );
        
        // Reset form and show success message
        setTimeout(() => {
          this.isLoading = false;
          this.toastr.success('SMS সফলভাবে পাঠানো হয়েছে!');
          this.clearForm();
        }, 2000);
      } catch (error) {
        this.isLoading = false;
        console.error('SMS sending error:', error);
        this.toastr.error('SMS পাঠাতে ত্রুটি হয়েছে!');
      }
    }
  }

  // Preview SMS
  previewSMS() {
    if (this.message && this.recipients) {
      this.isPreviewMode = true;
    }
  }

  // Close preview modal
  closePreviewModal() {
    this.isPreviewMode = false;
  }

  // Legacy localStorage support (fallback)
  private loadFromLocalStorage() {
    if (this.drafts.length === 0) { // Only if no backend drafts
      const saved = localStorage.getItem('sms_draft');
      if (saved) {
        try {
          const data = JSON.parse(saved);
          this.smsForm.patchValue(data);
        } catch (e) {
          console.warn('Could not load localStorage draft');
        }
      }
    }
  }
}



